import React, { FC, useState } from "react";
import { StatGroup, Stat, StatNumber, Card, HStack, Stack, Text, Progress, Avatar, Flex, Tooltip } from "@chakra-ui/react";
import { useFetch } from "use-http";
import { api } from "../../util/api";
import { FiClipboard, FiFileText, FiUsers } from "react-icons/fi";
import { percentage } from "../../util/number";
import { ERROR_MESSAGE, useNotify } from "../../hooks/useNotify";
import { Survey } from "../../components/Survey/types";
import HeadingSection from "../../components/HeadingSection";
import Loading from "../../components/Misc/Loading";
import { Link } from "react-router-dom";

interface SummaryAPI {
  surveys: Survey[];
  userCount: number;
  surveyCount: number;
  responseCount: number;
  responseGoal: number;
}

const HomePage: FC = () => {
  const notify = useNotify()
  const [activeSurveys, setActiveSurveys] = useState<Survey[]>([]);
  const [surveyCount, setSurveyCount] = useState<number>(0);
  const [userCount, setUserCount] = useState<number>(0);
  const [responseCount, setResponseCount] = useState<number>(0);
  const [responseGoal, setResponseGoal] = useState<number>(0);

  const {
    get,
    cache,
    response,
    loading,
    error,
  } = useFetch(`${api}/statistics/summary`);

  const refreshSummary = async () => {
    cache.clear();
    const summary: SummaryAPI = await get();
    if (!error && response.ok && summary) {
      setActiveSurveys(summary.surveys);
      setUserCount(summary.userCount);
      setResponseCount(summary.responseCount);
      setSurveyCount(summary.surveyCount);
      setResponseGoal(summary.responseGoal);
    } else {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  React.useEffect(() => {
    refreshSummary();
  }, []); // eslint-disable-line

  return (
    <Stack>
      <StatGroup justifyContent='start' maxW='container.sm'>
        <Card px={4} py={2} margin={2} flex={1}>
          <Stat>
            <HeadingSection
              icon={FiClipboard}
              title='Encuestas activas'
            />
            <Loading isLoading={loading} />
            <StatNumber>{!loading && `${surveyCount}`}</StatNumber>
          </Stat>
        </Card>

        <Card px={4} py={2} margin={2} flex={1}>
          <Stat>
            <HeadingSection
              icon={FiFileText}
              title='Respuestas'
            />
            <Loading isLoading={loading} />
            <StatNumber>{!loading && responseCount}</StatNumber>
          </Stat>
        </Card>
        <Card px={4} py={2} margin={2} flex={1}>
          <Stat>
            <HeadingSection
              icon={FiUsers}
              title='Brigadistas'
            />
            <Loading isLoading={loading} />
            <StatNumber>{!loading && userCount}</StatNumber>
          </Stat>
        </Card>

      </StatGroup>
      {activeSurveys.map((survey) => (
        <Stack maxW='container.sm' key={survey.id}>

          <Link to={`/encuestas/${survey.id}`} key={survey.id}>
            <Card px={4} py={2} margin={2}>
              <HStack justify='space-between'>
                <HeadingSection title={survey.title} />
                <HeadingSection
                  icon={FiUsers}
                  title={`${survey.responses} / ${responseGoal}`}
                />
              </HStack>
              <HStack>
                <Avatar />
                <Flex w='full' borderRadius='full' overflow='hidden'>
                  <Progress
                    w={'100%'}
                    height='32px'
                    borderRadius='none'
                    value={percentage(survey.responses, responseGoal)}
                  />
                </Flex>
                <Flex
                  minW='64px'
                  width='64px'
                  minH='64px'
                  height='64px'
                  bg='transparent'
                  borderRadius='50%'
                  justifyContent='center'
                  alignItems='center'
                  color='CaptionText'
                  cursor='default'
                >
                  <Tooltip label={`${percentage(survey.responses, responseGoal).toFixed(2)}%`}>
                    <Text fontSize='2xl'>
                      {`${Math.floor(percentage(survey.responses, responseGoal))}%`}
                    </Text>
                  </Tooltip>
                </Flex>
              </HStack>
            </Card>
          </Link>
        </Stack>
      ))}
    </Stack>
  );
}

export default HomePage
